import type { FilterDefinition } from "@/components/StructuredSearch/config/structured-search-schema";
import type { Filter } from "@/components/StructuredSearch/config/types";

const SPAN_ATTRIBUTES_FIELD = "span_attributes";
const SPAN_RESOURCE_FIELD = "span_resource";

const OLDER_DATA_SUPPORTED_FIELDS = new Set<string>([
  "trace_id",
  "trace_workflow_ids",
  SPAN_ATTRIBUTES_FIELD,
  SPAN_RESOURCE_FIELD,
]);

const NESTED_OLDER_DATA_ALLOWED_OPERATORS = [
  "key_equals",
  "key_not_equals",
  "key_contains",
  "key_not_contains",
  "in",
  "not_in",
  "is_empty",
  "is_not_empty",
] as const;

const WORKFLOW_OLDER_DATA_ALLOWED_OPERATORS = ["contains", "in"] as const;

const OLDER_DATA_ALLOWED_OPERATORS: Record<string, readonly string[]> = {
  trace_id: ["is", "is_not", "in", "not_in"],
  trace_workflow_ids: WORKFLOW_OLDER_DATA_ALLOWED_OPERATORS,
  [SPAN_ATTRIBUTES_FIELD]: NESTED_OLDER_DATA_ALLOWED_OPERATORS,
  [SPAN_RESOURCE_FIELD]: NESTED_OLDER_DATA_ALLOWED_OPERATORS,
};

export const OLDER_DATA_UNAVAILABLE_COPY =
  "Some filters are unavailable because this search is querying older data.";

export const isOlderDataSearch = (
  dateRange: { from?: Date; to?: Date } | undefined,
  cutoverDate: string | null | undefined,
): boolean => {
  if (!cutoverDate) {
    return true;
  }

  const parsedCutoverDate = new Date(cutoverDate);
  if (Number.isNaN(parsedCutoverDate.getTime())) {
    return true;
  }

  return (
    !!dateRange?.from &&
    !Number.isNaN(dateRange.from.getTime()) &&
    dateRange.from.getTime() < parsedCutoverDate.getTime()
  );
};

export const isPostgresOnlySearch = ({
  dateRange,
  cutoverDate,
}: {
  dateRange: { from?: Date; to?: Date } | undefined;
  cutoverDate: string | null | undefined;
}): boolean => isOlderDataSearch(dateRange, cutoverDate);

export const isOlderDataFieldSupported = (field: string | undefined): boolean =>
  !!field && OLDER_DATA_SUPPORTED_FIELDS.has(field);

export const getOlderDataAllowedOperators = (field: string | undefined): string[] =>
  field ? [...(OLDER_DATA_ALLOWED_OPERATORS[field] ?? [])] : [];

export const isOlderDataOperatorSupported = (
  field: string | undefined,
  operator: string | undefined,
): boolean =>
  !!field &&
  !!operator &&
  isOlderDataFieldSupported(field) &&
  (OLDER_DATA_ALLOWED_OPERATORS[field]?.includes(operator) ?? false);

export const isOlderDataCompatibleFilter = (filter: Filter): boolean =>
  isOlderDataOperatorSupported(filter.field, filter.operator);

export const sanitizeFiltersForOlderData = (filters: Filter[]): Filter[] =>
  filters.every(isOlderDataCompatibleFilter)
    ? filters
    : filters.filter(isOlderDataCompatibleFilter);

export const partitionFieldsByOlderDataSupport = (
  fields: FilterDefinition[],
): {
  supported: FilterDefinition[];
  unsupported: FilterDefinition[];
} =>
  fields.reduce(
    (result, field) => {
      if (isOlderDataFieldSupported(field.key)) {
        result.supported.push(field);
      } else {
        result.unsupported.push(field);
      }
      return result;
    },
    {
      supported: [] as FilterDefinition[],
      unsupported: [] as FilterDefinition[],
    },
  );
